/**
 * Green Klean® Chlorinated Disinfecting Tablets — Product Page
 * Includes 3-way comparison vs Clorox Healthcare Bleach Wipes & PDI Super Sani-Cloth
 * with Add-to-Quote-Cart integration.
 *
 * Mounted at:
 *   - /marketplace/green-klean        (public)
 *   - /hospital/marketplace/green-klean (hospital portal)
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ShoppingCart, CheckCircle2, X, ShieldCheck, Leaf,
  Droplets, FlaskConical, Clock, FileText, Phone, Download,
  Award, AlertTriangle, Beaker, Recycle, Star,
  Biohazard, Zap, TrendingDown, Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuoteCart } from "@/contexts/QuoteCartContext";
import SiteHeader from "@/components/SiteHeader";
import MarketplaceCategoryNav from "@/components/MarketplaceCategoryNav";
import MarketplaceBreadcrumb from "@/components/MarketplaceBreadcrumb";

// Brand assets — pulled directly from gogreenklean.com via headless-browser scrape
// and self-hosted under /api/static/green-kleen/* (mounted in backend/server.py).
const ASSET_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/static/green-kleen`;
const IMG = (p) => `${ASSET_BASE}/images/${p}`;
const PDF = (cat, p) => `${ASSET_BASE}/pdfs/${cat}/${p}`;

const GK_LOGO = IMG("gk-logo.png");
const GK_PRODUCT_HERO = IMG("gk-tablets-bottle.jpg");
const GK_TABLET_CIRCLE = IMG("gk-tablet-circle.png");

// ---------- Product Sales Sheet & SDS catalog (mirrors the 4 tabs on
// gogreenklean.com/product/chlorinated-disinfecting-tablets/) ----------
const SALES_SHEETS = [
  { thumb: IMG("thumb-hocl.jpg"),           file: PDF("sales_sheets", "chlorinated-tablets-hocl-flyer.pdf"),     title: "Hypochlorous Acid Tablet for Disinfecting" },
  { thumb: IMG("thumb-replace-bleach.jpg"), file: PDF("sales_sheets", "replace-bleach-safely.pdf"),              title: "Replace Bleach Safely" },
  { thumb: IMG("thumb-food-service.jpg"),   file: PDF("sales_sheets", "food-service-flyer.pdf"),                 title: "Chlorinated Disinfecting Tablets for Food Service" },
  { thumb: IMG("thumb-education.jpg"),      file: PDF("sales_sheets", "educational-facilities-flyer.pdf"),       title: "EPA Registered Disinfectant for Schools & Educational Facilities" },
  { thumb: IMG("thumb-ltc.jpg"),            file: PDF("sales_sheets", "long-term-care-flyer.pdf"),               title: "Safer Disinfecting for Long Term & Assisted Care" },
  { thumb: IMG("thumb-spanish.jpg"),        file: PDF("sales_sheets", "spanish-facilities-flyer.pdf"),           title: "Escuelas e Instituciones Educativas (Español)" },
  { thumb: IMG("thumb-toilet-bowl.jpg"),    file: PDF("sales_sheets", "toilet-bowl-flyer.pdf"),                  title: "Disinfect, Clean and Kill C. diff" },
];
const SDS_SHEETS = [
  { thumb: IMG("thumb-sds.jpg"), file: PDF("sds", "sds-cdt-in-use-dilution.pdf"), title: "SDS — Chlorinated Disinfecting Tablets (In-Use Dilution)" },
];
const QUATS_DANGER = [
  { file: PDF("quats_danger", "dangers-of-quats-safer-solution.pdf"),     title: "The Danger of Quats — A Safer Solution" },
  { file: PDF("quats_danger", "quats-toxic-effects-fact-sheet.pdf"),      title: "Quats Toxic Effects Fact Sheet" },
  { file: PDF("quats_danger", "quats-danger-public-spaces.pdf"),          title: "Danger of Quats in Public Spaces" },
  { file: PDF("quats_danger", "quats-not-around-children.pdf"),           title: "Quats Should Not Be Used Around Children" },
  { file: PDF("quats_danger", "quats-occupational-asthma.pdf"),           title: "Quats, Bleach, PAA and Occupational Asthma" },
  { file: PDF("quats_danger", "quats-decreased-activity.pdf"),            title: "Decreased Activity of Disinfectants Containing Quats" },
  { file: PDF("quats_danger", "quats-more-facts.pdf"),                    title: "More Facts on Quats" },
];

// ---------- Immersive section-break imagery ----------
// Full-bleed EVS / healthcare photography inserted between sections to give the
// page a cinematic rhythm. Each break fades into the surrounding section colors
// via top/bottom gradients so the image appears to "bleed" into the page.
//
// Photos: self-hosted under /images/green-kleen/breaks/*.jpg (public folder).
const BREAK_IMG = (name) => `/images/green-kleen/breaks/${name}`;

const ImageBreak = ({
  src,
  alt,
  kicker,
  title,
  subtitle,
  align = "left",
  fadeFrom = "from-white",
  fadeTo = "from-white",
  height = "h-[340px] sm:h-[420px] lg:h-[480px]",
  testId,
}) => (
  <section
    className={`relative w-full ${height} overflow-hidden`}
    data-testid={testId}
  >
    {/* Photograph */}
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="absolute inset-0 w-full h-full object-cover"
    />
    {/* Top fade — bleeds into previous section */}
    <div
      className={`absolute top-0 inset-x-0 h-24 sm:h-28 bg-gradient-to-b ${fadeFrom} to-transparent pointer-events-none`}
    />
    {/* Bottom fade — bleeds into next section */}
    <div
      className={`absolute bottom-0 inset-x-0 h-24 sm:h-28 bg-gradient-to-t ${fadeTo} to-transparent pointer-events-none`}
    />
    {/* Text overlay (only when title provided) */}
    {title && (
      <>
        <div
          className={`absolute inset-0 pointer-events-none ${
            align === "right"
              ? "bg-gradient-to-l from-slate-950/80 via-slate-950/40 to-transparent"
              : "bg-gradient-to-r from-slate-950/80 via-slate-950/40 to-transparent"
          }`}
        />
        <div
          className={`relative h-full max-w-7xl mx-auto px-6 flex items-center ${
            align === "right" ? "justify-end" : ""
          }`}
        >
          <div
            className={`max-w-xl ${align === "right" ? "text-right" : ""}`}
          >
            {kicker && (
              <span
                className="inline-block bg-emerald-500/90 text-white text-[10px] sm:text-[11px] font-extrabold tracking-[0.22em] uppercase mb-4 px-3 py-1.5 rounded-full backdrop-blur-sm"
                data-testid={testId ? `${testId}-kicker` : undefined}
              >
                {kicker}
              </span>
            )}
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight"
              style={{ textShadow: "0 2px 24px rgba(0,0,0,0.65)" }}
            >
              {title}
            </h2>
            {subtitle && (
              <p
                className="mt-4 text-white/95 text-base sm:text-lg leading-relaxed"
                style={{ textShadow: "0 1px 10px rgba(0,0,0,0.6)" }}
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>
      </>
    )}
  </section>
);

// Inline Green Klean® wordmark fallback (used in tight spots / mobile)
const GreenKleanWordmark = ({ className = "", inverse = false }) => (
  <span
    className={`inline-flex items-center gap-1.5 font-extrabold tracking-tight ${className}`}
    style={{ fontFamily: "'Outfit','Segoe UI',sans-serif" }}
  >
    <Leaf className={`w-5 h-5 ${inverse ? "text-emerald-300" : "text-emerald-600"}`} strokeWidth={2.5} />
    <span className={inverse ? "text-white" : "text-emerald-800"}>
      Green <span className={inverse ? "text-emerald-300" : "text-emerald-600"}>Kleen</span>
      <span className="align-super text-[0.6em] ml-0.5 opacity-80">®</span>
    </span>
  </span>
);

// ---------- Product catalog (single hero SKU + a 2-bottle case) ----------
const GK_PRODUCT = {
  erp: "GK-CDT6.55-2pkg",
  name: "Green Klean® Chlorinated Disinfecting Tablets — 6.55 g",
  short: "EPA-registered HOCl tablet — drop into tap water to make a non-corrosive, hospital-grade disinfectant",
  image: GK_PRODUCT_HERO,
  source: "green-klean",
  category: "Disinfecting & Sanitizing",
  packaging: "120 tablets / bottle · 2 bottles / case (240 tablets)",
  epa: "EPA Reg. No. 71847-6-91038",
  contactTimes: [
    { pathogen: "C. auris", time: "2 min" },
    { pathogen: "C. difficile spores", time: "4 min" },
    { pathogen: "Norovirus / SARS-CoV-2 / TB / MRSA", time: "≤ 4 min" },
  ],
};

// ---------- 4-way comparison data (Green Klean vs 3 wipe formulations) ----------
const COMPARE_COLS = [
  {
    id: "gk",
    name: "Green Klean® Chlorinated Disinfecting Tablets",
    sku: "GK-CDT6.55-2pkg",
    badge: "Recommended",
    badgeClass: "bg-emerald-600",
    accent: "emerald",
  },
  {
    id: "ssc",
    name: "PDI Super Sani-Cloth® Germicidal Wipe",
    sku: "Q55172 (160-ct)",
    accent: "slate",
  },
  {
    id: "scb",
    name: "PDI Sani-Cloth® Bleach Germicidal Wipe",
    sku: "P54072 (75-ct)",
    accent: "slate",
  },
  {
    id: "clx",
    name: "Clorox Healthcare® Bleach Germicidal Wipes",
    sku: "EPA 67619-12",
    accent: "slate",
  },
];

const COMPARE_ROWS = [
  {
    label: "EPA Reg. #",
    icon: ShieldCheck,
    values: ["71847-6-91038", "9480-4", "9480-8", "67619-12"],
  },
  {
    label: "Active Ingredient",
    icon: FlaskConical,
    values: [
      "Hypochlorous Acid (HOCl) — generated on-site from sodium dichloroisocyanurate tablets",
      "Quaternary ammonium + Isopropyl Alcohol (IPA)",
      "1:10 dilution of 6,300 ppm Sodium Hypochlorite (bleach)",
      "0.55% Sodium Hypochlorite (5,500 ppm bleach)",
    ],
  },
  {
    label: "Contact Time — C. auris",
    icon: Clock,
    values: ["2 minutes", "2 minutes", "4 minutes", "3 minutes"],
    highlights: [true, true, false, false],
  },
  {
    label: "Contact Time — C. difficile spores",
    icon: Clock,
    values: ["4 minutes", "Not on label", "4 minutes", "3 minutes"],
    highlights: [true, false, true, true],
  },
  {
    label: "Kill Claims",
    icon: Award,
    values: [
      "Broad spectrum — Norovirus, SARS-CoV-2, Hep A/B/C, Aspergillus, C. diff, C. auris, TB, MRSA, GRSA + EPA Food Sanitizer & Fungicide",
      "34 microorganisms — Bactericidal, Tuberculocidal, Virucidal, Fungicidal (incl. C. auris, SARS-CoV-2)",
      "52 microorganisms — Sporicidal, Bactericidal, Tuberculocidal, Virucidal, Fungicidal (incl. C. diff, C. auris, Norovirus)",
      "60+ pathogens including C. diff spores, C. auris, MRSA, VRE, Norovirus, Monkeypox",
    ],
  },
  {
    label: "Format",
    icon: Beaker,
    values: [
      "Dissolvable tablet → mixes 1 tablet to make ready-to-use disinfectant in tap water",
      "Pre-saturated quat/IPA wipe (canister / softpack / individual packet)",
      "Pre-saturated bleach wipe (canister / pail / individual packet)",
      "Pre-saturated bleach wipe (canister)",
    ],
  },
  {
    label: "Shelf Life",
    icon: Clock,
    values: ["3 years (tablet)", "24 months", "11 months", "11 months"],
    highlights: [true, false, false, false],
  },
  {
    label: "HMIS Safety Rating (in-use solution)",
    icon: ShieldCheck,
    values: [
      "Triple Zero (0/0/0)",
      "Eye irritant — Cat. 1",
      "Bleach — eye irritant + corrosive to soft surfaces",
      "Bleach — eye/skin irritant",
    ],
    highlights: [true, false, false, false],
  },
  {
    label: "Surface Compatibility",
    icon: CheckCircle2,
    values: [
      "Non-corrosive — safe on stainless, plastics, soft surfaces, food-contact surfaces",
      "Compatible with most healthcare equipment (~150+ OEMs validated)",
      "Bleach — degrades plastics, stainless, soft goods over time",
      "Bleach — degrades plastics, stainless, soft goods over time",
    ],
    highlights: [true, true, false, false],
  },
  {
    label: "Contains Quats",
    icon: AlertTriangle,
    values: [
      "No",
      "Yes — Alkyl C12-18 Dimethyl benzyl/ethylbenzyl ammonium chloride",
      "No",
      "No",
    ],
    highlights: [true, false, true, true],
  },
  {
    label: "EPA Food-Contact Sanitizer",
    icon: Leaf,
    values: ["Yes", "No", "No", "No"],
    highlights: [true, false, false, false],
  },
  {
    label: "Air-Freight Shippable",
    icon: Recycle,
    values: ["Yes — solid tablet, non-flammable", "No (IPA — flammable, ground only)", "Yes — bleach, ground/air OK", "Yes — bleach, ground/air OK"],
    highlights: [true, false, true, true],
  },
  {
    label: "Plastic / Waste Footprint",
    icon: Recycle,
    values: [
      "Minimal — 240 tablets ship in 2 small bottles → 240+ gallons of disinfectant",
      "Hundreds of saturated wipes per canister; canister + film waste",
      "75 wipes per canister; canister + film waste",
      "Hundreds of saturated wipes per canister; canister + film waste",
    ],
    highlights: [true, false, false, false],
  },
  {
    label: "Cost-per-Use",
    icon: Droplets,
    values: [
      "Pennies per ready-to-use quart — concentrate economics",
      "$0.06–$0.12 per wipe (varies by SKU)",
      "$0.18–$0.30 per wipe (bleach formulations cost more)",
      "$0.10–$0.18 per wipe",
    ],
    highlights: [true, false, false, false],
  },
];

// ---------- Marketing pillars ----------
const PILLARS = [
  {
    icon: ShieldCheck,
    title: "EPA Registered Disinfectant",
    body: "EPA #71847-6-91038. Registered as Disinfectant, Food Sanitizer and Fungicide — one product, three approvals.",
  },
  {
    icon: FlaskConical,
    title: "HOCl — CDC's Most Effective Disinfectant",
    body: "Per CDC: \"The advantage of HOCl over hypochlorites — it exerts a more prolonged bactericidal effect.\" Each tablet delivers an exact, consistent dose.",
  },
  {
    icon: Leaf,
    title: "HMIS Triple-Zero in Solution",
    body: "No PPE drama. No respiratory or skin sensitizers. Safe to use around children, residents and food-prep areas.",
  },
  {
    icon: Recycle,
    title: "240 Gallons in a Box",
    body: "2-bottle case (240 tablets) replaces dozens of cases of pre-mixed wipes & RTU bottles — cut shipping, storage & plastic waste.",
  },
];

// ---------- Sub-components ----------
const PathogenChip = ({ name }) => (
  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
    <CheckCircle2 className="w-3 h-3" /> {name}
  </span>
);

const ComparisonCell = ({ value, highlight, accent }) => (
  <td
    className={`px-4 py-4 align-top text-sm leading-relaxed border-l border-slate-200 ${
      accent === "emerald" ? "bg-emerald-50/40" : "bg-white"
    } ${highlight ? "font-semibold text-emerald-800" : "text-slate-700"}`}
  >
    <div className="flex items-start gap-2">
      {highlight && <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />}
      <span>{value}</span>
    </div>
  </td>
);

// ---------- Tabbed Resources (mirrors the 4 tabs on gogreenklean.com) ----------
const TABS = [
  { id: "description",   label: "Description" },
  { id: "sales-sheets",  label: "Product Sales Sheets" },
  { id: "sds",           label: "SDS Sheet" },
  { id: "quats",         label: "The Danger of Quats" },
];

const PdfThumbCard = ({ thumb, file, title, idx, prefix }) => (
  <a
    href={file}
    target="_blank"
    rel="noopener noreferrer"
    className="group flex flex-col bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-emerald-400 hover:shadow-lg transition-all"
    data-testid={`${prefix}-${idx}`}
    download
  >
    {thumb && (
      <div className="aspect-[4/5] bg-slate-50 overflow-hidden">
        <img
          src={thumb}
          alt={title}
          className="w-full h-full object-contain p-3 group-hover:scale-[1.02] transition-transform"
        />
      </div>
    )}
    <div className="p-4 flex items-start justify-between gap-3">
      <p className="text-sm font-semibold text-slate-800 leading-tight flex-1 line-clamp-3">{title}</p>
      <Download className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0 group-hover:translate-y-0.5 transition-transform" />
    </div>
  </a>
);

function ResourceTabs() {
  const [active, setActive] = useState("description");
  return (
    <section
      className="bg-gradient-to-b from-slate-50 to-white border-y border-slate-200 py-16"
      data-testid="resource-tabs-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">Product Documents</h2>
          <p className="text-slate-600">All resources sourced directly from Green Klean® and self-hosted on Sterile Mate.</p>
        </div>

        {/* Tab nav */}
        <div
          className="flex flex-wrap justify-center gap-2 mb-10 border-b border-slate-200"
          data-testid="resource-tabs-nav"
        >
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              data-testid={`resource-tab-${t.id}`}
              className={`px-5 py-3 -mb-px border-b-2 text-sm font-semibold transition-all ${
                active === t.id
                  ? "border-emerald-600 text-emerald-700"
                  : "border-transparent text-slate-500 hover:text-emerald-700 hover:border-emerald-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {active === "description" && (
          <div className="max-w-4xl mx-auto bg-white rounded-2xl border border-slate-200 p-8 shadow-sm" data-testid="tab-panel-description">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Description</h3>
            <div className="space-y-4 text-slate-700 leading-relaxed">
              <p>
                Green Klean® <strong>Chlorinated Disinfecting Tablets</strong> are an EPA-registered
                broad-spectrum <strong>Disinfectant, Food Sanitizer and Fungicide</strong>. A one-tablet
                system for powerful and effective killing ability without any negatives, creating a
                much safer and healthier environment.
              </p>
              <p>
                EPA registered to kill <strong>Norovirus, SARS-CoV-2, Hepatitis A/B/C, Aspergillus
                fumigatus, C. difficile (4 min), C. auris (2 min), TB, MRSA, GRSA</strong>, and many
                more pathogens. <strong>120 tablets per bottle, 2 bottles per case (240 tablets total).</strong>
              </p>
              <p>
                The tablets create a solution of <strong>HOCl (Hypochlorous Acid)</strong> when added
                to tap water — and per CDC: <em>"The advantage of HOCl over hypochlorites — it exerts
                a more prolonged bactericidal effect."</em>
              </p>
              <ul className="grid sm:grid-cols-2 gap-2 pt-2">
                {[
                  "Part #: GK-CDT6.55-2pkg",
                  "EPA Reg. No. 71847-6-91038",
                  "EPA Disinfectant + Food Sanitizer + Fungicide",
                  "HMIS Triple-Zero in solution (0/0/0)",
                  "3-year shelf life",
                  "Non-corrosive, non-toxic, no quats, no bleach",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {active === "sales-sheets" && (
          <div data-testid="tab-panel-sales-sheets">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {SALES_SHEETS.map((s, i) => (
                <PdfThumbCard key={i} {...s} idx={i} prefix="sales-sheet" />
              ))}
            </div>
          </div>
        )}

        {active === "sds" && (
          <div data-testid="tab-panel-sds" className="max-w-md mx-auto">
            {SDS_SHEETS.map((s, i) => (
              <PdfThumbCard key={i} {...s} idx={i} prefix="sds-sheet" />
            ))}
          </div>
        )}

        {active === "quats" && (
          <div data-testid="tab-panel-quats" className="max-w-4xl mx-auto">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
              <p className="text-slate-700 leading-relaxed">
                <strong>Quaternary ammonium compounds (Quats)</strong> — commonly used in disinfectants —
                have been linked to <strong>respiratory irritation, skin sensitization, and potential
                endocrine disruption</strong> with prolonged exposure. Their overuse can also contribute
                to <strong>antimicrobial resistance</strong> and harmful environmental buildup.
              </p>
              <p className="text-slate-700 mt-3">
                Below are reference documents — see for yourself why Green Klean® Chlorinated Tablets
                are the <strong>safer</strong> and <strong>more effective</strong> choice.
              </p>
            </div>
            <div className="space-y-2">
              {QUATS_DANGER.map((q, i) => (
                <a
                  key={i}
                  href={q.file}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  data-testid={`quats-doc-${i}`}
                  className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 hover:border-amber-400 hover:shadow-md transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-amber-700" />
                    </div>
                    <span className="font-semibold text-slate-800 text-sm">{q.title}</span>
                  </div>
                  <Download className="w-5 h-5 text-amber-600 group-hover:translate-y-0.5 transition-transform" />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ---------- Main page ----------
export default function GreenKleenPage() {
  const { addItem, setShowCart, cart } = useQuoteCart();

  // Ensure deep-linked landings (e.g. from email campaigns) open at the top
  // of the page regardless of any previous scroll position or hash.
  useEffect(() => {
    if (!window.location.hash) {
      window.scrollTo(0, 0);
    }
  }, []);

  const handleAddTablets = () => {
    addItem({
      erp: GK_PRODUCT.erp,
      name: GK_PRODUCT.name,
      image: GK_PRODUCT.image,
      source: "green-klean",
      category: GK_PRODUCT.category,
    });
  };

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/40 via-white to-emerald-50/40" data-testid="green-kleen-page">
      <SiteHeader />
      <MarketplaceCategoryNav currentCategory="green-klean" />
      <MarketplaceBreadcrumb trail={[{ label: "Green Klean®" }]} />
      {/* Top bar */}
      <header className="bg-gradient-to-r from-emerald-900 via-emerald-800 to-teal-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/marketplace"
            className="flex items-center gap-2 text-emerald-200 hover:text-white transition-colors"
            data-testid="back-to-marketplace-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back to Marketplace</span>
          </Link>
          <div className="flex items-center gap-3">
            <a href="tel:8009883892" className="hidden sm:flex items-center gap-1.5 text-sm text-emerald-200 hover:text-white">
              <Phone className="w-4 h-4" /> 800.988.3892
            </a>
            <Button
              onClick={() => setShowCart(true)}
              size="sm"
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold relative"
              data-testid="open-quote-cart-btn"
            >
              <ShoppingCart className="w-4 h-4 mr-1.5" />
              Quote Cart
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center" data-testid="quote-cart-count">
                  {cartCount}
                </span>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600 text-white">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 30%, white 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div>
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              <div className="bg-white rounded-lg px-4 py-2.5 inline-flex items-center shadow-md" data-testid="gk-hero-logo-pill">
                <img
                  src={GK_LOGO}
                  alt="Green Klean®"
                  className="h-7 md:h-8 w-auto"
                  data-testid="gk-hero-logo-img"
                />
              </div>
              <Badge className="bg-emerald-400/30 text-emerald-50 border border-emerald-300/40 backdrop-blur">
                EPA #71847-6-91038
              </Badge>
              <Badge className="bg-amber-400/30 text-amber-50 border border-amber-300/40 backdrop-blur" data-testid="authorized-distributor-badge">
                Sterile Mate · Authorized Distributor
              </Badge>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] mb-6" style={{ fontFamily: "'Outfit','Segoe UI',sans-serif" }}>
              We think <em className="text-emerald-200 not-italic font-black">differently</em><br />
              about clean.
            </h1>
            <p className="text-lg text-emerald-50/90 leading-relaxed max-w-xl mb-8">
              Green Klean® <strong>Chlorinated Disinfecting Tablets</strong> drop into tap water to create
              a multipurpose, hospital-grade <strong>HOCl</strong> disinfecting solution — kills
              <strong> C. auris in 2 min</strong> and <strong>C. difficile spores in 4 min</strong>, with
              an <strong>HMIS triple-zero</strong> safety rating in solution.
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              <Button
                size="lg"
                onClick={() => { handleAddTablets(); setShowCart(true); }}
                className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold shadow-xl"
                data-testid="hero-add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Quote Cart
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={handleAddTablets}
                className="border-white/40 bg-white/10 hover:bg-white/20 text-white"
                data-testid="hero-quick-add-btn"
              >
                Quick Add (no checkout)
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              <PathogenChip name="C. auris (2 min)" />
              <PathogenChip name="C. difficile (4 min)" />
              <PathogenChip name="Norovirus" />
              <PathogenChip name="SARS-CoV-2" />
              <PathogenChip name="Hep A/B/C" />
              <PathogenChip name="MRSA · GRSA" />
              <PathogenChip name="TB" />
              <PathogenChip name="Aspergillus" />
            </div>
          </div>

          {/* Product image / data-card */}
          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-to-br from-emerald-300/30 to-teal-200/30 rounded-3xl blur-2xl" />
            <div className="relative bg-white rounded-3xl p-6 shadow-2xl">
              <div className="relative">
                <img
                  src={GK_PRODUCT_HERO}
                  alt="Green Klean® Chlorinated Disinfecting Tablets — bottle of 120"
                  className="w-full h-80 object-contain"
                  data-testid="hero-product-image"
                />
                {/* Badge pill overlaid on photo */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-amber-400 text-slate-900 text-xs font-extrabold px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap">
                  EPA-Registered Disinfectant
                </div>
              </div>
              <div className="mt-6 grid grid-cols-3 gap-3">
                <div className="text-center bg-emerald-50 rounded-lg py-3">
                  <p className="text-[10px] text-emerald-700 font-semibold uppercase tracking-wider">Tablets/Bottle</p>
                  <p className="text-2xl font-extrabold text-emerald-900">120</p>
                </div>
                <div className="text-center bg-emerald-50 rounded-lg py-3">
                  <p className="text-[10px] text-emerald-700 font-semibold uppercase tracking-wider">Bottles/Case</p>
                  <p className="text-2xl font-extrabold text-emerald-900">2</p>
                </div>
                <div className="text-center bg-emerald-50 rounded-lg py-3">
                  <p className="text-[10px] text-emerald-700 font-semibold uppercase tracking-wider">Shelf Life</p>
                  <p className="text-2xl font-extrabold text-emerald-900">3 yr</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== IMAGE BREAK #1 — hospital hallway bleeds from hero into pillars ===== */}
      <ImageBreak
        testId="break-hospital-hallway"
        src={BREAK_IMG("hospital-hallway.jpg")}
        alt="Evening corridor of a modern hospital ward, softly lit and freshly cleaned"
        fadeFrom="from-teal-600"
        fadeTo="from-white"
        align="left"
        kicker="Healthcare-grade HOCl"
        title={<>Every hallway. <span className="text-emerald-300">Every shift.</span></>}
        subtitle="From patient rooms to surgical suites — one tablet scales across your entire footprint without Quats, bleach fumes, or PPE drama."
      />

      {/* PILLARS */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">Why hospitals are switching to tablets</h2>
          <p className="text-slate-600 text-lg">Same kill claims. Lower cost. Less plastic. Safer for staff.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PILLARS.map((p, i) => (
            <div
              key={i}
              className="group bg-white rounded-2xl p-6 border border-slate-200 hover:border-emerald-400 hover:shadow-xl transition-all"
              data-testid={`pillar-card-${i}`}
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <p.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">{p.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== IMAGE BREAK #2 — patient room between pillars and pathogen problem ===== */}
      <ImageBreak
        testId="break-patient-room"
        src={BREAK_IMG("patient-room.jpg")}
        alt="An empty, freshly turned-over patient room with bedrails, call button, and bathroom in frame"
        fadeFrom="from-white"
        fadeTo="from-emerald-50"
        align="right"
        kicker="Where outbreaks hide"
        title={<>Toilets, bedrails, <span className="text-emerald-300">call buttons.</span></>}
        subtitle="The high-touch reservoirs where C. diff and C. auris survive for months. Green Klean® HOCl erases them in as little as 3 minutes."
      />

      {/* HEALTHCARE-FOCUSED: Toilets as hidden pathogen reservoirs + C. diff / C. auris kill claims */}
      <section id="healthcare-toilet-problem" className="bg-gradient-to-b from-emerald-50 via-white to-emerald-50/60 py-20" data-testid="healthcare-problem-section">
        <div className="max-w-7xl mx-auto px-6">
          {/* Problem statement */}
          <div className="max-w-4xl mx-auto text-center mb-14">
            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 mb-4" data-testid="healthcare-badge">
              <Biohazard className="w-3 h-3 mr-1.5 inline" /> Hidden Pathogens in Healthcare Settings
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-5 leading-tight text-slate-900">
              Every flush is an <span className="text-emerald-700">aerosol plume</span>.<br className="hidden sm:block" />
              The toilet is the reservoir most hospitals miss.
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-5">
              University of Colorado Boulder researchers used high-powered lasers to capture the invisible —
              a single flush ejects <strong className="text-slate-900">thousands of droplets</strong> up to 5 feet into the air in under 8 seconds,
              carrying <em>C. difficile</em>, <em>C. auris</em>, norovirus and other hardy pathogens onto every surface in the room.
              For hospitals, long-term care, and nursing homes, this isn't theoretical — it's the mechanism behind countless
              HAIs and outbreak clusters.
            </p>
            <p className="text-sm text-slate-500 italic">
              Source: John Crimaldi, Professor of Civil, Environmental and Architectural Engineering, University of Colorado Boulder ·
              Persistence of Bowl Water Contamination — <a href="https://pubmed.ncbi.nlm.nih.gov/29651169/" target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:text-emerald-800 underline font-semibold">PubMed 29651169</a>
            </p>
          </div>

          {/* Kill Time Hero Stats */}
          <div className="grid sm:grid-cols-3 gap-5 mb-16 max-w-5xl mx-auto" data-testid="kill-time-stats">
            <div className="relative bg-white border-2 border-emerald-200 rounded-2xl p-6 text-center shadow-md hover:shadow-xl hover:border-emerald-400 transition-all">
              <div className="absolute top-3 right-3 text-[9px] font-extrabold tracking-wider uppercase text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full border border-emerald-200">Nightmare Pathogen</div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 mx-auto mb-3 flex items-center justify-center shadow-md">
                <Biohazard className="w-7 h-7 text-white" />
              </div>
              <p className="text-sm font-bold text-slate-900 mb-1">Clostridioides difficile</p>
              <p className="text-5xl font-extrabold text-emerald-700 mb-1">4 <span className="text-2xl">min</span></p>
              <p className="text-xs text-slate-600 leading-tight">Eradicated with 2 tablets — daily use prevents spore regrowth</p>
            </div>
            <div className="relative bg-white border-2 border-emerald-200 rounded-2xl p-6 text-center shadow-md hover:shadow-xl hover:border-emerald-400 transition-all">
              <div className="absolute top-3 right-3 text-[9px] font-extrabold tracking-wider uppercase text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full border border-emerald-200">Global Health Threat</div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 mx-auto mb-3 flex items-center justify-center shadow-md">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <p className="text-sm font-bold text-slate-900 mb-1">Candida auris</p>
              <p className="text-5xl font-extrabold text-emerald-700 mb-1">2 <span className="text-2xl">min</span></p>
              <p className="text-xs text-slate-600 leading-tight">Multidrug-resistant fungus — killed in half the typical dwell time</p>
            </div>
            <div className="relative bg-white border-2 border-emerald-200 rounded-2xl p-6 text-center shadow-md hover:shadow-xl hover:border-emerald-400 transition-all">
              <div className="absolute top-3 right-3 text-[9px] font-extrabold tracking-wider uppercase text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full border border-emerald-200">Highly Contagious</div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-700 mx-auto mb-3 flex items-center justify-center shadow-md">
                <AlertTriangle className="w-7 h-7 text-white" />
              </div>
              <p className="text-sm font-bold text-slate-900 mb-1">Norovirus</p>
              <p className="text-5xl font-extrabold text-emerald-700 mb-1">19M+</p>
              <p className="text-xs text-slate-600 leading-tight">Annual U.S. cases · survives alcohol sanitizer — 2 weeks on surfaces</p>
            </div>
          </div>

          {/* The Room Turnover Protocol */}
          <div className="max-w-5xl mx-auto mb-16">
            <div className="text-center mb-8">
              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 mb-3">
                <CheckCircle2 className="w-3 h-3 mr-1.5 inline" /> The Simple Solution
              </Badge>
              <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Daily Toilet Disinfection = Room Turnover Protocol</h3>
              <p className="text-slate-600 text-base">Break the C. diff spore lifecycle before it can seed the next flush cluster.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div className="bg-white border-2 border-emerald-200 rounded-2xl p-6 shadow-sm">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shrink-0 shadow-sm">
                    <Droplets className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-0.5">Step 1 · Daily</p>
                    <h4 className="text-lg font-bold text-slate-900">Drop 2 tablets in every toilet bowl</h4>
                  </div>
                </div>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /><span>Each tablet weighs 6.55 g — zero measuring, zero training</span></li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /><span>Tablets fizz &amp; dissolve, releasing HOCl (hypochlorous acid)</span></li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /><span>Neutral pH — penetrates cell walls, doesn't repel pathogens</span></li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /><span>Brush bowl, let dwell <strong>4 minutes for C. diff</strong>, flush before use</span></li>
                </ul>
              </div>

              <div className="bg-white border-2 border-emerald-200 rounded-2xl p-6 shadow-sm">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shrink-0 shadow-sm">
                    <TrendingDown className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-0.5">Step 2 · Compounding</p>
                    <h4 className="text-lg font-bold text-slate-900">Daily repetition breaks the spore cycle</h4>
                  </div>
                </div>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /><span>C. diff spores <strong>cannot re-colonize</strong> bowl water between daily treatments</span></li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /><span>Eliminates the "persistent bowl contamination" documented in PubMed 29651169</span></li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /><span>Protects room turnover — patient rooms, nursing home bathrooms, public restrooms</span></li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /><span>Safe for staff — no quats, no PAA, no concentrated bleach exposure</span></li>
                </ul>
              </div>
            </div>
          </div>

          {/* Why HOCl beats Quats / Bleach / Hydrogen Peroxide */}
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 mb-3">
                <FlaskConical className="w-3 h-3 mr-1.5 inline" /> The Science
              </Badge>
              <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">HOCl outperforms what most hospitals use today</h3>
              <p className="text-slate-600 text-base">Most facilities are still on quats (Virex 256, Diversey Crew) or hydrogen-peroxide products (Oxivir TB, Oxycide) — here's why NaDCC-based HOCl is the upgrade.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              {/* Quats */}
              <div className="bg-white border-2 border-slate-200 rounded-xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center">
                    <X className="w-4 h-4 text-slate-500" />
                  </div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">What Quats Do</p>
                </div>
                <p className="text-sm font-bold text-slate-900 mb-2">Bind to microfiber &amp; become useless</p>
                <ul className="space-y-1.5 text-xs text-slate-600">
                  <li>• Absorbed into the cloth, not transferred to the surface (Boyce, 2016)</li>
                  <li>• Designated asthmagens (AOEC exposure code 322.321)</li>
                  <li>• Linked to reproductive toxicity in published mouse studies</li>
                  <li>• Drive antimicrobial resistance — quat-resistant microbes cross-resist antibiotics</li>
                </ul>
              </div>

              {/* Bleach / HP */}
              <div className="bg-white border-2 border-slate-200 rounded-xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center">
                    <AlertTriangle className="w-4 h-4 text-slate-500" />
                  </div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">What Bleach / HP Do</p>
                </div>
                <p className="text-sm font-bold text-slate-900 mb-2">Risky, unstable, worker-hazardous</p>
                <ul className="space-y-1.5 text-xs text-slate-600">
                  <li>• Bleach expires in 6–12 months (often already degraded on shelf)</li>
                  <li>• Oxivir TB <strong>NOT effective vs. C. diff</strong> (hospitals stock bleach anyway)</li>
                  <li>• PAA (peracetic acid) triggered NIOSH worker-safety investigations</li>
                  <li>• One major U.S. hospital banned staff from handling PAA entirely</li>
                </ul>
              </div>

              {/* Green Klean */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-xl p-5 shadow-md relative overflow-hidden">
                <div className="absolute -right-8 -top-8 w-24 h-24 bg-emerald-200/40 rounded-full blur-2xl" />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-lg bg-emerald-600 flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">What Green Klean® Does</p>
                  </div>
                  <p className="text-sm font-bold text-slate-900 mb-2">Neutral-pH HOCl — safe, stable, lethal to pathogens</p>
                  <ul className="space-y-1.5 text-xs text-slate-700">
                    <li>• <strong>Most powerful oxidant</strong> in the chlorine family — more than bleach</li>
                    <li>• No sticky residue, no microfiber binding — 100% transfers to surface</li>
                    <li>• Cited in CDC 2008 Guideline for Disinfection &amp; Sterilization in Healthcare Facilities</li>
                    <li>• Also food-grade — no rinse required in food-service areas</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Ops efficiency — the sustainability + logistics story */}
          <div className="mt-14 max-w-5xl mx-auto bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-7 md:p-8 shadow-lg">
            <div className="grid md:grid-cols-[auto_1fr] gap-6 items-center">
              <div className="text-center shrink-0">
                <div className="w-20 h-20 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center mx-auto mb-2">
                  <Recycle className="w-10 h-10 text-white" />
                </div>
                <p className="text-4xl font-extrabold text-white">90%</p>
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald-100">Less of everything</p>
              </div>
              <div>
                <h4 className="text-xl font-bold text-white mb-2">Stop shipping water. Start shipping tablets.</h4>
                <p className="text-sm text-emerald-50 leading-relaxed mb-3">
                  One tablet = one quart of ready-to-use disinfectant. Switching from liquid bleach / quat jugs to
                  Green Klean® NaDCC tablets slashes <strong className="text-white">shipping costs, CO₂, plastic, cardboard, pallet, and fuel consumption</strong> by
                  approximately 90% — a measurable sustainability win your ESG team will love.
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-[11px]">
                  {["Shipping","CO₂","Plastic","Cardboard","Pallets","Fuel"].map((x) => (
                    <div key={x} className="bg-white/15 backdrop-blur rounded-lg py-1.5 px-2 text-center">
                      <p className="font-bold text-white">−90%</p>
                      <p className="text-emerald-100">{x}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* COST SAVINGS — Green Klean vs Clorox Healthcare Bleach */}
          <div className="mt-14 max-w-6xl mx-auto" data-testid="cost-savings-section">
            <div className="text-center mb-8">
              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 mb-3">
                💰 Dollars &amp; Cents
              </Badge>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 text-slate-900">
                <span className="text-emerald-700">87% less</span> per usable gallon than Clorox Healthcare Bleach
              </h3>
              <p className="text-slate-600 text-base max-w-2xl mx-auto">
                Same hospital-grade efficacy against C. diff and Norovirus — at a fraction of the cost, with 3× the shelf life
                and one-tenth the storage footprint.
              </p>
            </div>

            {/* Hero price-per-gallon comparison */}
            <div className="grid md:grid-cols-[1fr_auto_1fr] items-stretch gap-4 mb-8">
              {/* Green Klean column */}
              <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-6 text-center relative overflow-hidden shadow-lg" data-testid="cost-gk-card">
                <div className="absolute top-3 right-3 text-[9px] font-extrabold tracking-wider uppercase bg-white text-emerald-700 px-2 py-0.5 rounded-full shadow-sm">Winner</div>
                <p className="text-xs font-bold text-emerald-100 uppercase tracking-wider mb-1">Green Klean® GK-CDT</p>
                <p className="text-xs text-emerald-100/90 mb-3">6.55 g tablets · 2-pkg case</p>
                <p className="text-6xl font-extrabold text-white mb-1">$3.65<span className="text-xl text-emerald-100">/gal</span></p>
                <p className="text-xs text-emerald-100/90 mb-4">$219 / case · 60 gallons yield @ 4 tabs/gal</p>
                <div className="border-t border-white/25 pt-3 space-y-1 text-[11px] text-left text-emerald-50">
                  <p><strong className="text-white">Format:</strong> Effervescent tablets</p>
                  <p><strong className="text-white">Active:</strong> Sodium dichloroisocyanurate (NaDCC)</p>
                  <p><strong className="text-white">Dilution:</strong> 4 tablets per gallon of water</p>
                  <p><strong className="text-white">Shelf life:</strong> 3 years</p>
                  <p><strong className="text-white">pH:</strong> Neutral (6.5–7) — equipment-safe</p>
                </div>
              </div>

              {/* VS savings card */}
              <div className="md:w-48 flex md:flex-col items-center justify-center bg-white border-2 border-emerald-300 rounded-2xl p-5 text-center shadow-md" data-testid="cost-savings-callout">
                <p className="text-[10px] font-extrabold tracking-[0.2em] uppercase text-emerald-600 mb-1">You Save</p>
                <p className="text-5xl font-black text-emerald-700 leading-none">$24.35</p>
                <p className="text-[11px] font-semibold text-emerald-600 mt-1 mb-3">per gallon</p>
                <div className="border-t border-emerald-200 pt-3 w-full">
                  <p className="text-3xl font-extrabold text-emerald-700 leading-none">87%</p>
                  <p className="text-[10px] uppercase tracking-wider text-emerald-600 mt-0.5 font-bold">Cost Reduction</p>
                </div>
              </div>

              {/* Clorox column */}
              <div className="bg-slate-100 border-2 border-slate-200 rounded-2xl p-6 text-center" data-testid="cost-clorox-card">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Clorox Healthcare</p>
                <p className="text-xs text-slate-400 mb-3">Bleach Germicidal · Ready-to-Use</p>
                <p className="text-6xl font-extrabold text-slate-600 mb-1">$28.00<span className="text-xl text-slate-400">/gal</span></p>
                <p className="text-xs text-slate-400 mb-4">$28 / gal · 1 gallon per jug</p>
                <div className="border-t border-slate-300 pt-3 space-y-1 text-[11px] text-left text-slate-500">
                  <p><strong className="text-slate-700">Format:</strong> Ready-to-use liquid</p>
                  <p><strong className="text-slate-700">Active:</strong> Sodium hypochlorite</p>
                  <p><strong className="text-slate-700">Dilution:</strong> Pre-mixed (water weight)</p>
                  <p><strong className="text-slate-700">Shelf life:</strong> <span className="text-slate-600">12 months</span> (degrades)</p>
                  <p><strong className="text-slate-700">pH:</strong> Highly alkaline · corrosive</p>
                </div>
              </div>
            </div>

            {/* Four key advantages */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="cost-advantages-grid">
              <div className="bg-white border border-emerald-200 rounded-xl p-4 shadow-sm">
                <TrendingDown className="w-6 h-6 text-emerald-600 mb-2" />
                <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1">Massive Cost Savings</p>
                <p className="text-xs text-slate-600 leading-snug">At roughly <strong className="text-slate-900">$3.65/gallon</strong>, you pay <strong className="text-slate-900">~87% less</strong> than pre-mixed liquid Clorox Healthcare.</p>
              </div>
              <div className="bg-white border border-emerald-200 rounded-xl p-4 shadow-sm">
                <Clock className="w-6 h-6 text-emerald-600 mb-2" />
                <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1">Superior Stability</p>
                <p className="text-xs text-slate-600 leading-snug"><strong className="text-slate-900">3-year shelf life</strong> vs. 12 months for liquid bleach — which loses potency over time.</p>
              </div>
              <div className="bg-white border border-emerald-200 rounded-xl p-4 shadow-sm">
                <Recycle className="w-6 h-6 text-emerald-600 mb-2" />
                <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1">Easier Storage</p>
                <p className="text-xs text-slate-600 leading-snug">One case of tablets replaces <strong className="text-slate-900">60 heavy 1-gallon jugs</strong> — reducing storage space by <strong className="text-slate-900">90%</strong>.</p>
              </div>
              <div className="bg-white border border-emerald-200 rounded-xl p-4 shadow-sm">
                <ShieldCheck className="w-6 h-6 text-emerald-600 mb-2" />
                <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1">Safer Formulation</p>
                <p className="text-xs text-slate-600 leading-snug">pH-neutral (<strong className="text-slate-900">6.5–7</strong>) solution — <strong className="text-slate-900">less corrosive</strong> to hospital equipment &amp; surfaces.</p>
              </div>
            </div>

            {/* Facility-scale savings calculator */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-6 md:p-7 shadow-lg" data-testid="facility-savings-estimate">
              <div className="grid md:grid-cols-[auto_1fr_auto] items-center gap-6">
                <div className="text-center">
                  <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center mx-auto mb-1">
                    <Beaker className="w-8 h-8 text-white" />
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-100">Scaled Savings</p>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-white mb-1">What this looks like for a mid-size facility</h4>
                  <p className="text-sm text-emerald-50 leading-relaxed">
                    A 200-bed hospital running <strong className="text-white">50 gallons of disinfectant per week</strong> for room turnover,
                    restrooms, and common areas spends <strong className="text-white">~$72,800/yr</strong> on Clorox Healthcare —
                    vs. <strong className="text-white">~$9,490/yr</strong> on Green Klean Tablets.
                  </p>
                </div>
                <div className="text-center shrink-0">
                  <p className="text-3xl md:text-4xl font-black text-white leading-none">$63K+</p>
                  <p className="text-[10px] uppercase tracking-wider text-emerald-100 font-bold mt-1">Saved / year</p>
                </div>
              </div>
            </div>

            {/* Also-available secondary SKU — 120-Tablet Jar at $110.00 */}
            <div className="mt-6 grid sm:grid-cols-2 gap-3 max-w-3xl mx-auto">
              <div className="bg-white border-2 border-emerald-200 rounded-xl p-4 text-center shadow-sm">
                <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-1">Smaller Green Klean SKU</p>
                <p className="text-sm text-slate-900 font-semibold mb-1">120-Tablet Jar</p>
                <p className="text-2xl font-extrabold text-emerald-700">$110.00</p>
                <p className="text-[11px] text-slate-600 mt-1"><strong className="text-emerald-700">30-gallon yield</strong> (4 tabs/gal) · just <strong className="text-emerald-700">$3.67/gal</strong></p>
                <p className="text-[10px] text-slate-500 mt-0.5">Ideal for outpatient clinics &amp; LTC</p>
              </div>
              <div className="bg-slate-100 border-2 border-slate-200 rounded-xl p-4 text-center">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Clorox Comparable Refill</p>
                <p className="text-sm text-slate-700 font-semibold mb-1">128 oz (1-gal) Bottle</p>
                <p className="text-2xl font-extrabold text-slate-500">$24.58</p>
                <p className="text-[11px] text-slate-500 mt-1">1-gallon yield · expires in 12 months</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-14 text-center">
            <Button
              onClick={() => setShowCart(true)}
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-base px-8 py-6 rounded-full shadow-xl shadow-emerald-500/30"
              data-testid="healthcare-problem-cta"
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              Request a Quote for Your Facility
            </Button>
            <p className="text-xs text-slate-500 mt-3">
              <Users className="w-3 h-3 inline mr-1" />
              Ask about volume pricing for hospital systems, LTC networks, and EVS service contractors
            </p>
          </div>
        </div>
      </section>

      {/* ===== IMAGE BREAK #3 — EVS floor scrubbing bleeds into vacuum bag product line ===== */}
      <ImageBreak
        testId="break-evs-floor-scrub"
        src={BREAK_IMG("evs-floor-scrub.jpg")}
        alt="Environmental Services worker scrubbing a hospital floor with a microfiber mop and bucket"
        fadeFrom="from-emerald-50"
        fadeTo="from-slate-50"
        align="left"
        kicker="EVS armed differently"
        title={<>One tablet. One bucket. <span className="text-emerald-300">One wing.</span></>}
        subtitle="No respirator. No mixing. No measuring. Drop-in chlorinated tablets deliver repeatable, EPA-registered kill claims across an entire EVS cart — bucket after bucket."
      />

      {/* VACUUM BAGS & FILTERS — sister product line */}
      <section className="bg-gradient-to-br from-slate-50 to-emerald-50/30 border-y border-emerald-100 py-16" data-testid="vacuum-bags-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div>
            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 mb-3">
              Also from Green Klean® · 125+ SKUs
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3 leading-tight">
              Premium Vacuum Bags &amp; Filters
            </h2>
            <p className="text-slate-700 leading-relaxed mb-5 text-lg">
              Engineered for professionals and trusted in high-demand environments — premium 2 &amp; 3-ply
              construction with <strong>Micro-Plus filtration</strong> capturing up to <strong>99.9% of dust,
              pollen, and allergens</strong>. OEM-equivalent for Advance, ProTeam, Windsor, Karcher, Hoover,
              Dyson, Bissell, Sanitaire, Nilfisk and 16+ more brands.
            </p>
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-white rounded-xl p-3 border border-emerald-200 text-center">
                <p className="text-2xl font-extrabold text-emerald-700">99.9%</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Filtration</p>
              </div>
              <div className="bg-white rounded-xl p-3 border border-emerald-200 text-center">
                <p className="text-2xl font-extrabold text-emerald-700">125+</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">SKUs</p>
              </div>
              <div className="bg-white rounded-xl p-3 border border-emerald-200 text-center">
                <p className="text-2xl font-extrabold text-emerald-700">25+</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">OEM Brands</p>
              </div>
            </div>
            <Link
              to="/marketplace/green-klean/vacuum-bags-filters"
              data-testid="vacuum-bags-learn-more-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-md hover:shadow-xl transition-all"
            >
              Learn More about Vacuum Bags &amp; Filters →
            </Link>
          </div>
          {/* PDF cover preview tile */}
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-emerald-200/40 to-teal-200/40 rounded-3xl blur-2xl" />
            <div className="relative bg-white rounded-2xl p-6 shadow-xl border border-emerald-100">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/static/green-kleen/vacuum-bags/thumbs/Bag-Filter-Sales-Flyer-8-25-1.jpg`}
                alt="Green Klean Vacuum Bags & Filters Sales Flyer"
                className="w-full rounded-xl shadow-md"
                data-testid="vacuum-bags-preview-img"
              />
              <p className="text-xs text-slate-500 text-center mt-3">Tap "Learn More" for the full lineup, sales sheets &amp; warranty.</p>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON CHART */}
      <section className="bg-slate-900 text-white py-20" data-testid="comparison-chart-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge className="bg-amber-500/20 text-amber-300 border border-amber-400/30 mb-4">Head-to-head</Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4">
              Green Klean® <span className="text-emerald-300">vs</span> Super Sani-Cloth® <span className="text-emerald-300">vs</span> Sani-Cloth® Bleach <span className="text-emerald-300">vs</span> Clorox Healthcare®
            </h2>
            <p className="text-slate-300 text-lg">
              Independent comparison sourced from each manufacturer's published EPA labels and product pages.
            </p>
          </div>

          <div className="bg-white text-slate-800 rounded-2xl shadow-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1200px]" data-testid="comparison-table">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="px-4 py-5 text-left text-xs uppercase tracking-wider text-slate-500 w-56">Spec</th>
                    {COMPARE_COLS.map((col) => (
                      <th
                        key={col.id}
                        className={`px-4 py-5 text-left border-l border-slate-200 ${
                          col.accent === "emerald" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-800"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          {col.badge && (
                            <Badge className={`${col.badgeClass} text-white text-[10px] uppercase`}>
                              <Star className="w-2.5 h-2.5 mr-1 fill-current" /> {col.badge}
                            </Badge>
                          )}
                        </div>
                        <p className="font-bold leading-tight text-sm">{col.name}</p>
                        <p className={`text-xs mt-1 ${col.accent === "emerald" ? "text-emerald-100" : "text-slate-500"}`}>
                          SKU {col.sku}
                        </p>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {COMPARE_ROWS.map((row, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-4 py-4 align-top w-56">
                        <div className="flex items-start gap-2">
                          <row.icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                          <span className="font-semibold text-slate-800 text-sm">{row.label}</span>
                        </div>
                      </td>
                      {row.values.map((val, ci) => (
                        <ComparisonCell
                          key={ci}
                          value={val}
                          highlight={row.highlights?.[ci]}
                          accent={COMPARE_COLS[ci].accent}
                        />
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50">
                    <td className="px-4 py-5"></td>
                    <td className="px-4 py-5 border-l border-slate-200 bg-emerald-50/40">
                      <Button
                        onClick={() => { handleAddTablets(); setShowCart(true); }}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="compare-add-gk-btn"
                      >
                        <ShoppingCart className="w-4 h-4 mr-2" /> Add Green Klean to Quote
                      </Button>
                    </td>
                    <td className="px-4 py-5 border-l border-slate-200 text-center text-xs text-slate-500">
                      Sourced from PDI Healthcare<br />pdihc.com
                    </td>
                    <td className="px-4 py-5 border-l border-slate-200 text-center text-xs text-slate-500">
                      Sourced from PDI Healthcare<br />pdihc.com
                    </td>
                    <td className="px-4 py-5 border-l border-slate-200 text-center text-xs text-slate-500">
                      Sourced from CloroxPro<br />cloroxpro.com
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <p className="text-center text-xs text-slate-400 mt-6 max-w-3xl mx-auto">
            Pathogen kill-claim, EPA registration and contact-time data is current as of the most recent
            published EPA master labels for each product. Always reference the manufacturer's label
            for full directions for use.
          </p>
        </div>
      </section>

      {/* ===== IMAGE BREAK #4 — cleaning cart bleeds from dark comparison into white contact times ===== */}
      <ImageBreak
        testId="break-cleaning-cart"
        src={BREAK_IMG("cleaning-cart.jpg")}
        alt="Hospital housekeeping cart stocked with supplies in a sunlit corridor"
        fadeFrom="from-slate-900"
        fadeTo="from-white"
        align="right"
        kicker="One SKU. Infinite buckets."
        title={<>The cart that replaces a <span className="text-emerald-300">closet of chemicals.</span></>}
        subtitle="Swap wipes, pails, concentrates, and spray bottles for a single jar of tablets. Freight drops, storage footprint shrinks, and inventory SKUs collapse by 80%."
      />

      {/* CONTACT TIMES & DETAILED SPEC PANEL */}
      {/* CONTACT TIMES */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Clock className="w-6 h-6 text-emerald-600" />
            <h3 className="text-2xl font-bold text-slate-900">Contact Times</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {GK_PRODUCT.contactTimes.map((c, i) => (
              <div key={i} className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                <span className="font-semibold text-emerald-900 text-sm">{c.pathogen}</span>
                <Badge className="bg-emerald-600 text-white text-base px-3 py-1">{c.time}</Badge>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-6">
            HOCl solution must remain visibly wet on the surface for the labeled contact time to deliver
            the EPA-approved kill claim. Re-wet as needed.
          </p>
        </div>
      </section>

      {/* TABBED RESOURCES — mirrors the 4 tabs on gogreenklean.com */}
      <ResourceTabs />


      {/* QUATS WARNING + CTA */}
      <section className="bg-gradient-to-br from-amber-50 to-orange-50 border-y border-amber-200 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-[auto_1fr] gap-6 items-start">
            <div className="w-16 h-16 rounded-2xl bg-amber-500 flex items-center justify-center flex-shrink-0 shadow-lg">
              <AlertTriangle className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
                The hidden cost of <span className="text-amber-700">Quats</span>
              </h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                Quaternary ammonium compounds (Quats) — the active ingredient in most disposable
                disinfecting wipes — have been linked to <strong>respiratory irritation, skin sensitization,
                potential endocrine disruption</strong>, and contribute to antimicrobial resistance.
                Green Klean® tablets generate <strong>HOCl</strong> from a chlorine donor — the same
                disinfectant your immune system already produces. <strong>No quats. No bleach. No PPE drama.</strong>
              </p>
              <p className="text-sm text-slate-500 italic">
                Sources: NIOSH, OSHA, peer-reviewed literature on Quat-exposed healthcare workers — full reading list
                on the GoGreenKlean resource page.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== IMAGE BREAK #5 — gloved hands wiping surface transitions to Final CTA ===== */}
      <ImageBreak
        testId="break-gloves-wipe"
        src={BREAK_IMG("gloves-wipe.jpg")}
        alt="Gloved hands wiping down a stainless surface with a microfiber cloth"
        fadeFrom="from-amber-50"
        fadeTo="from-white"
        align="left"
        kicker="90 seconds to wet-contact kill"
        title={<>Spray. <span className="text-emerald-300">Wait.</span> Done.</>}
        subtitle="HOCl is the same disinfectant your immune system already makes. Safe on hands, safe on patients, safe in enclosed rooms — and EPA-registered against the pathogens that matter."
      />

      {/* FINAL CTA */}
      <section className="max-w-5xl mx-auto px-6 py-16 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
          Ready to switch your facility to tablets?
        </h2>
        <p className="text-slate-600 text-lg mb-8 max-w-2xl mx-auto">
          Add Green Klean® Chlorinated Disinfecting Tablets to your quote cart and the Sterile Mate team
          will respond within 2 business hours with shipping &amp; volume pricing.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            size="lg"
            onClick={() => { handleAddTablets(); setShowCart(true); }}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-xl"
            data-testid="final-cta-add-btn"
          >
            <ShoppingCart className="w-5 h-5 mr-2" />
            Add to Quote Cart
          </Button>
          <Button
            size="lg"
            variant="outline"
            asChild
            className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
          >
            <a href="tel:8009883892" data-testid="final-cta-call-btn">
              <Phone className="w-5 h-5 mr-2" />
              Call 800.988.3892
            </a>
          </Button>
        </div>
      </section>

      {/* Cart dialog (modal) */}
    </div>
  );
}
